import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const DEFAULT_TEMPLATE_ID = 'e8ed9b61-301d-45af-9a13-b1677f068bf8'

function formatDate(dateValue) {
  if (!dateValue) return '—'
  const date = new Date(dateValue)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  })
}

function formatDateTime(dateValue) {
  if (!dateValue) return '—'
  const date = new Date(dateValue)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  })
}

function formatReadableDateTime(dateValue) {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(date)

  const get = (type) => parts.find(p => p.type === type)?.value || ''
  return `${get('month')} ${get('day')}, ${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')} ${get('timeZoneName')}`.trim()
}

function resolveTemplate(body, fields) {
  return (body || '')
    .replaceAll('{{client_name}}', fields.clientName || '')
    .replaceAll('{{client_address}}', fields.clientAddress || '')
    .replaceAll('{{date}}', fields.date || '')
    .replaceAll('{{tax_year}}', String(fields.taxYear || ''))
}

function renderDocumentBody(text, { emptyMessage = 'No letter body available.' } = {}) {
  const normalized = String(text || '').replace(/\r\n/g, '\n').trim()
  if (!normalized) {
    return <p style={{ margin: 0, fontSize: 15, color: '#6B7280' }}>{emptyMessage}</p>
  }

  const paragraphs = normalized.split(/\n\s*\n+/)

  return paragraphs.map((paragraph, pIndex) => {
    const lines = paragraph.split('\n')
    return (
      <p key={`p-${pIndex}`} style={{ margin: '0 0 1em', fontSize: 16, lineHeight: 1.7, color: '#1F2937' }}>
        {lines.map((line, lineIndex) => (
          <span key={`l-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    )
  })
}

function dedupeLettersById(rows) {
  const map = new Map()
  ;(rows || []).forEach((row) => {
    if (!row?.id) return
    if (!map.has(row.id)) map.set(row.id, row)
  })
  return Array.from(map.values())
}

function SignatureCanvas({ clearToken, onChange }) {
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const lastPos = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#202326'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    onChange(null)
  }, [clearToken, onChange])

  function getPos(e) {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height)
    }
  }

  function handlePointerDown(e) {
    e.preventDefault()
    drawing.current = true
    lastPos.current = getPos(e)
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) {}
  }

  function handlePointerMove(e) {
    if (!drawing.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pos = getPos(e)
    ctx.beginPath()
    ctx.moveTo(lastPos.current.x, lastPos.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    lastPos.current = pos
  }

  function handlePointerEnd() {
    if (!drawing.current) return
    drawing.current = false
    onChange(canvasRef.current.toDataURL('image/png'))
  }

  return (
    <canvas
      ref={canvasRef}
      width={520}
      height={160}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerLeave={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      style={{
        width: '100%',
        height: 160,
        border: '2px solid var(--border)',
        borderRadius: 8,
        background: '#fff',
        cursor: 'crosshair',
        touchAction: 'none'
      }}
    />
  )
}

export default function EngagementsPage() {
  const { profile } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const [engagements, setEngagements] = useState([])
  const [clients, setClients] = useState([])
  const [defaultTemplate, setDefaultTemplate] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedSignatures, setSelectedSignatures] = useState([])
  const [mode, setMode] = useState('detail')
  const [selectedClientId, setSelectedClientId] = useState('')
  const [signerName, setSignerName] = useState('')
  const [signatureData, setSignatureData] = useState(null)
  const [clearToken, setClearToken] = useState(0)

  const selectedEngagement = useMemo(() => engagements.find(e => e.id === selectedId) || null, [engagements, selectedId])
  const selectedClient = useMemo(() => clients.find(c => c.id === selectedClientId) || null, [clients, selectedClientId])
  const cpaSignature = useMemo(() => selectedSignatures.find(s => s.signer_role === 'cpa') || null, [selectedSignatures])
  const taxpayerSignature = useMemo(() => selectedSignatures.find(s => s.signer_role === 'taxpayer') || null, [selectedSignatures])
  const spouseSignature = useMemo(() => selectedSignatures.find(s => s.signer_role === 'spouse') || null, [selectedSignatures])
  const acceptedClientSignatures = useMemo(() => [taxpayerSignature, spouseSignature].filter(Boolean), [taxpayerSignature, spouseSignature])
  const taxProfessionalSignedAt = cpaSignature?.signed_at || selectedEngagement?.sent_at || selectedEngagement?.created_at || ''

  const firmName = profile?.practice_name || "Brown's Accounting & Tax Service Co"
  const firmAddress = [
    profile?.business_address,
    profile?.address,
    [profile?.street_address, profile?.apartment].filter(Boolean).join(', '),
    [[profile?.city, profile?.state].filter(Boolean).join(', '), profile?.zip_code].filter(Boolean).join(' '),
  ].filter(Boolean)[0] || 'Address on file'
  const firmPhone = profile?.phone || 'Phone on file'

  useEffect(() => {
    if (!signerName && profile?.full_name) setSignerName(profile.full_name)
  }, [profile?.full_name, signerName])

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      setError('')

      try {
        const [lettersRes, clientsRes, templateRes] = await Promise.all([
          supabase
            .from('engagement_letters')
            .select('*, clients(id, full_name, business_name)')
            .eq('cpa_id', profile.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('clients')
            .select('*')
            .eq('cpa_id', profile.id)
            .order('business_name', { ascending: true }),
          supabase
            .from('engagement_templates')
            .select('id, name, subject, body, tax_year, is_default')
            .eq('cpa_id', profile.id)
            .eq('is_default', true)
            .maybeSingle(),
        ])

        if (lettersRes.error) throw lettersRes.error
        if (clientsRes.error) throw clientsRes.error

        let template = null
        if (templateRes.error && templateRes.error.code !== 'PGRST116') throw templateRes.error
        if (templateRes.data) template = templateRes.data

        if (!template) {
          const fallbackRes = await supabase
            .from('engagement_templates')
            .select('id, name, subject, body, tax_year, is_default')
            .eq('id', DEFAULT_TEMPLATE_ID)
            .maybeSingle()

          if (!fallbackRes.error) template = fallbackRes.data
        }

        const letterData = dedupeLettersById(lettersRes.data || [])
        setEngagements(letterData)
        setClients(clientsRes.data || [])
        setDefaultTemplate(template)

        if (letterData.length > 0) {
          setSelectedId(letterData[0].id)
          setMode('detail')
        } else {
          setMode('new')
        }
      } catch (err) {
        setError(err.message || 'Unable to load engagements.')
      }

      setLoading(false)
    }

    if (profile?.id) loadData()
  }, [profile?.id])

  useEffect(() => {
    async function loadSignatures() {
      if (!selectedId || mode !== 'detail') {
        setSelectedSignatures([])
        return
      }

      const { data, error: sigError } = await supabase
        .from('engagement_signatures')
        .select('*')
        .eq('engagement_letter_id', selectedId)
        .order('signed_at', { ascending: true })

      if (!sigError) setSelectedSignatures(data || [])
    }

    loadSignatures()
  }, [selectedId, mode])

  const renderedPreview = useMemo(() => {
    if (!defaultTemplate || !selectedClient) return ''

    const line1 = [selectedClient.street_address, selectedClient.apartment].filter(Boolean).join(', ')
    const cityState = [selectedClient.city, selectedClient.state].filter(Boolean).join(', ')
    const line2 = [cityState, selectedClient.zip_code].filter(Boolean).join(' ')
    const clientAddress = [line1, line2].filter(Boolean).join('\n')

    return resolveTemplate(defaultTemplate.body, {
      clientName: selectedClient.full_name || selectedClient.business_name || 'Client',
      clientAddress,
      date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      taxYear: selectedClient.tax_year || defaultTemplate.tax_year || new Date().getFullYear(),
    })
  }, [defaultTemplate, selectedClient])

  async function refreshEngagements(selectId = null) {
    const { data, error: queryError } = await supabase
      .from('engagement_letters')
      .select('*, clients(id, full_name, business_name)')
      .eq('cpa_id', profile.id)
      .order('created_at', { ascending: false })

    if (queryError) {
      setError(queryError.message)
      return
    }

    const rows = dedupeLettersById(data || [])
    setEngagements(rows)

    if (selectId) {
      setSelectedId(selectId)
    } else if (rows.length > 0 && !selectedId) {
      setSelectedId(rows[0].id)
    }
  }

  async function handleSend() {
    if (!selectedClientId || !defaultTemplate || !renderedPreview || !signatureData || !signerName.trim()) {
      setToast('Select a client, draw signature, and provide signer name.')
      return
    }

    setSaving(true)
    setError('')

    try {
      const sentAt = new Date().toISOString()
      const title = `${selectedClient?.tax_year || defaultTemplate.tax_year || new Date().getFullYear()} Tax Engagement Letter`

      const { data: insertedLetter, error: letterError } = await supabase
        .from('engagement_letters')
        .insert({
          cpa_id: profile.id,
          client_id: selectedClientId,
          title,
          body: renderedPreview,
          status: 'sent',
          sent_at: sentAt,
        })
        .select('*')
        .single()

      if (letterError) throw letterError

      const { error: signatureError } = await supabase
        .from('engagement_signatures')
        .insert({
          engagement_letter_id: insertedLetter.id,
          signer_role: 'cpa',
          signer_name: signerName.trim(),
          signature_data: signatureData,
          signed_at: sentAt,
        })

      if (signatureError) throw signatureError

      // Notify client by email (fire-and-forget, don't block on result)
      supabase.auth.getSession().then(({ data: { session } }) => {
        fetch('/.netlify/functions/notify-client', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({
            type: 'engagement_letter_sent',
            client_id: selectedClientId,
            data: { title }
          })
        }).catch(() => {}) // non-blocking
      })

      await refreshEngagements(insertedLetter.id)
      setMode('detail')
      setSelectedClientId('')
      setSignatureData(null)
      setClearToken(v => v + 1)
      setToast('Engagement sent successfully.')
    } catch (err) {
      setError(err.message || 'Unable to send engagement.')
    }

    setSaving(false)
  }

  function downloadLetter(engagement) {
    const blob = new Blob([engagement.body || ''], { type: 'text/plain;charset=utf-8' })
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = `${engagement.title || 'engagement-letter'}.txt`
    a.click()
    URL.revokeObjectURL(href)
  }

  const readyToSend = !!selectedClientId && !!defaultTemplate && !!signatureData && signerName.trim().length > 1

  return (
    <div>
      <style>{`
        @media print {
          @page { margin: 1in; }
          html, body {
            background: #fff !important;
            color: #000 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: none !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
            background: #fff !important;
            color: #000 !important;
          }
          .print-area .print-letter-body,
          .print-area .print-letter-body p {
            font-family: Georgia, "Times New Roman", serif !important;
            color: #000 !important;
            font-size: 12pt !important;
            line-height: 1.65 !important;
          }
          .print-area .signature-image {
            width: 2in !important;
            max-width: 2in !important;
            height: auto !important;
            object-fit: contain !important;
          }
          .print-area .signature-block {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 0.25in;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="card" style={{ padding: '20px 24px', marginBottom: 18, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>Engagements</h2>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
              Manage engagement letters, signatures, and status.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }} className="no-print">
            <button className="btn btn-ghost" onClick={() => setMode('detail')} disabled={!selectedEngagement}>View Detail</button>
            <button className="btn btn-primary" onClick={() => setMode('new')}>+ New Engagement</button>
          </div>
        </div>
      </div>

      {toast && (
        <div style={{ marginBottom: 14, background: '#DBEAFE', border: '1px solid #93C5FD', color: '#1E3A8A', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
          {toast}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 14, background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 700 }}>All Engagement Letters</div>
        {loading ? (
          <div style={{ padding: 16, display: 'grid', gap: 8 }}>
            {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 48 }} />)}
          </div>
        ) : engagements.length === 0 ? (
          <div style={{ padding: 18, color: '#62615D', fontSize: 13 }}>No engagement letters yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F7F3EB' }}>
                  {['Client', 'Title', 'Status', 'Sent', 'Signed'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {engagements.map(letter => {
                  const active = letter.id === selectedId && mode === 'detail'
                  const statusBadge = letter.status === 'signed' ? 'success' : letter.status === 'sent' ? 'info' : letter.status === 'declined' ? 'danger' : 'neutral'
                  const clientName = letter.clients?.full_name || letter.clients?.business_name || 'Unknown client'

                  return (
                    <tr
                      key={letter.id}
                      onClick={() => { setSelectedId(letter.id); setMode('detail') }}
                      style={{ cursor: 'pointer', background: active ? '#F1E8D7' : '#fff', borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>{clientName}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{letter.title || 'Engagement Letter'}</td>
                      <td style={{ padding: '10px 14px' }}><span className={`badge badge-${statusBadge}`}>{letter.status}</span></td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>{formatDate(letter.sent_at)}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>{formatDate(letter.signed_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {mode === 'new' && (
        <div className="card no-print" style={{ padding: 18, marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, marginBottom: 12 }}>New Engagement</h3>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', marginBottom: 12 }}>
            <div>
              <label className="label">Client</label>
              <select className="input" value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)}>
                <option value="">Select client…</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.full_name || client.business_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Template</label>
              <input className="input" value={defaultTemplate?.name || 'Default Template'} disabled />
            </div>

            <div>
              <label className="label">Tax Professional Signer Name</label>
              <input className="input" value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Tax Professional full name" />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="label">Tax Professional Signature</label>
            <SignatureCanvas clearToken={clearToken} onChange={setSignatureData} />
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#62615D' }}>{signatureData ? 'Signature captured' : 'Draw Tax Professional signature above'}</span>
              <button className="btn btn-ghost" onClick={() => { setClearToken(v => v + 1); setSignatureData(null) }} style={{ fontSize: 12 }}>Clear</button>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label className="label">Live Preview (Resolved Letter)</label>
            <div className="card" style={{ padding: 14, background: '#fffdf8', minHeight: 240, fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {renderDocumentBody(renderedPreview, { emptyMessage: 'Select a client to preview the resolved engagement letter.' })}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setMode('detail')} disabled={!selectedEngagement}>Cancel</button>
            <button className="btn btn-primary" disabled={!readyToSend || saving} onClick={handleSend}>{saving ? 'Sending…' : 'Send Engagement'}</button>
          </div>
        </div>
      )}

      {mode === 'detail' && selectedEngagement && (
        <div className="card" style={{ padding: 18 }}>
          <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <h3 style={{ fontSize: 18, marginBottom: 2 }}>{selectedEngagement.title || 'Engagement Letter'}</h3>
              <div style={{ fontSize: 12, color: '#62615D' }}>
                {selectedEngagement.clients?.full_name || selectedEngagement.clients?.business_name || 'Unknown client'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }} className="no-print">
              <span className={`badge badge-${selectedEngagement.status === 'signed' ? 'success' : selectedEngagement.status === 'sent' ? 'info' : 'neutral'}`}>{selectedEngagement.status}</span>
              {(selectedEngagement.status === 'signed' || selectedEngagement.status === 'sent') && (
                <>
                  <button className="btn btn-ghost" onClick={() => window.print()}>Print</button>
                  {selectedEngagement.status === 'signed' && <button className="btn btn-primary" onClick={() => downloadLetter(selectedEngagement)}>Download</button>}
                </>
              )}
            </div>
          </div>

          <div className="card no-print" style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
              <div style={{ fontSize: 12 }}><strong>Sent:</strong> {formatDateTime(selectedEngagement.sent_at)}</div>
              <div style={{ fontSize: 12 }}><strong>Signed:</strong> {formatDateTime(selectedEngagement.signed_at)}</div>
            </div>
          </div>

          <div className="card no-print" style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Signatures</div>
            {selectedSignatures.length === 0 ? (
              <div style={{ fontSize: 12, color: '#62615D' }}>No signatures recorded yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {selectedSignatures.map(sig => (
                  <div key={sig.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{sig.signer_role}: {sig.signer_name}</div>
                      <div style={{ fontSize: 12, color: '#62615D' }}>{formatDateTime(sig.signed_at)}</div>
                    </div>
                    {sig.signature_data && <img src={sig.signature_data} alt={`${sig.signer_role} signature`} style={{ marginTop: 8, height: 48, maxWidth: 220, objectFit: 'contain' }} />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card print-area" style={{ padding: '28px 34px', background: '#fff' }}>
            <div style={{ borderBottom: '2px solid #111', paddingBottom: 12, marginBottom: 20 }}>
              <h1 style={{ fontSize: 30, marginBottom: 6 }}>{firmName}</h1>
              <div style={{ fontSize: 14, lineHeight: 1.4 }}>
                <div>{firmAddress}</div>
                <div>Phone: {firmPhone}</div>
              </div>
            </div>

            <h2 style={{ fontSize: 28, marginBottom: 6 }}>Engagement Letter</h2>
            <div style={{ fontSize: 14, marginBottom: 18, color: '#1F2937' }}>
              {selectedEngagement.title || 'Tax Preparation Engagement'}
            </div>

            <div className="print-letter-body" style={{ marginBottom: 24, fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {renderDocumentBody(selectedEngagement.body)}
            </div>

            <div style={{ borderTop: '1px solid #333', paddingTop: 14, marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, marginBottom: 12 }}>Tax Professional Signature Block</h3>
              <div className="signature-block" style={{ minWidth: 260 }}>
                <div style={{ minHeight: 72, display: 'flex', alignItems: 'flex-end', marginBottom: 2 }}>
                  {cpaSignature?.signature_data ? (
                    <img className="signature-image" src={cpaSignature.signature_data} alt="Tax Professional signature" style={{ maxWidth: 220, height: 56, objectFit: 'contain' }} />
                  ) : (
                    <div style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive', fontSize: 34, color: '#1f2937', lineHeight: 1 }}>
                      {cpaSignature?.signer_name || profile?.full_name || 'Marcelle Brown'}
                    </div>
                  )}
                </div>
                <div style={{ borderBottom: '1px solid #111', marginBottom: 6, minHeight: 2 }} />
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{cpaSignature?.signer_name || profile?.full_name || 'Marcelle Brown'}</div>
                <div style={{ fontSize: 12, marginBottom: 3, color: '#1F2937' }}>{firmName}</div>
                <div style={{ fontSize: 12, marginBottom: 10, color: '#1F2937' }}>{formatReadableDateTime(taxProfessionalSignedAt)}</div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #333', paddingTop: 14 }}>
              <h3 style={{ fontSize: 18, marginBottom: 12 }}>Accepted By</h3>
              {acceptedClientSignatures.length === 0 ? (
                <div style={{ color: '#1F2937', fontSize: 12 }}>No client signatures recorded yet.</div>
              ) : (
                <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                  {acceptedClientSignatures.map((sig) => (
                    <div key={sig.id} className="signature-block" style={{ minWidth: 260 }}>
                      <div style={{ minHeight: 72, display: 'flex', alignItems: 'flex-end', marginBottom: 2 }}>
                        {sig.signature_data ? (
                          <img className="signature-image" src={sig.signature_data} alt={`${sig.signer_role} signature`} style={{ maxWidth: 220, height: 56, objectFit: 'contain' }} />
                        ) : null}
                      </div>
                      <div style={{ borderBottom: '1px solid #111', marginBottom: 6, minHeight: 2 }} />
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{sig.signer_name || sig.signer_role}</div>
                      <div style={{ fontSize: 12, marginBottom: 3, color: '#1F2937', textTransform: 'capitalize' }}>{sig.signer_role}</div>
                      <div style={{ fontSize: 12, marginBottom: 10, color: '#1F2937' }}>{formatDateTime(sig.signed_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {mode === 'detail' && !selectedEngagement && !loading && (
        <div className="card" style={{ padding: 16, fontSize: 13, color: '#62615D' }}>
          Select an engagement row to view details, or create a new engagement.
        </div>
      )}
    </div>
  )
}
